import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (email, password, phone, coppaConsent) =>
    api.post('/api/auth/signup', {
      email,
      password,
      phone,
      coppa_consent: coppaConsent
    }),

  login: (email, password) =>
    api.post('/api/auth/login', { email, password }),

  getMe: () =>
    api.get('/api/auth/me'),
};

// Children API
export const childrenAPI = {
  getAll: () =>
    api.get('/api/children'),

  getById: (childId) =>
    api.get(`/api/children/${childId}`),

  create: (name, age) =>
    api.post('/api/children', { name, age }),

  update: (childId, data) =>
    api.put(`/api/children/${childId}`, data),

  delete: (childId) =>
    api.delete(`/api/children/${childId}`),
};

// Profile API
export const profileAPI = {
  get: (childId) =>
    api.get(`/api/children/${childId}/profile`),

  update: (childId, profileData) =>
    api.put(`/api/children/${childId}/profile`, profileData),

  partialUpdate: (childId, profileData) =>
    api.patch(`/api/children/${childId}/profile`, profileData),
};

// Conversation API
export const conversationAPI = {
  sendMessage: (childId, message, sessionId = null) =>
    api.post(`/api/conversations/${childId}/messages`, { message, session_id: sessionId }),

  getHistory: (childId, limit = 50, offset = 0) =>
    api.get(`/api/conversations/${childId}/history`, {
      params: { limit, offset },
    }),

  deleteHistory: (childId) =>
    api.delete(`/api/conversations/${childId}/history`),
};

// Insights API
export const insightsAPI = {
  get: (childId, limit = 20, insightType = null) =>
    api.get(`/api/children/${childId}/insights`, {
      params: { limit, insight_type: insightType },
    }),
  getSummary: (childId, days = 30) =>
    api.get(`/api/children/${childId}/insights/summary`, {
      params: { days }
    })
};

// Safety Events API
export const safetyEventsAPI = {
  get: (childId, limit = 50, eventType = null) =>
    api.get(`/api/children/${childId}/safety-events`, {
      params: { limit, event_type: eventType },
    }),
};

// Summaries API
export const summariesAPI = {
  generate: (childId, date = null) =>
    api.post(`/api/summaries/${childId}/generate`, { date }),

  getAll: (childId, limit = 30, startDate = null, endDate = null) =>
    api.get(`/api/summaries/${childId}/summaries`, {
      params: { limit, start_date: startDate, end_date: endDate },
    }),

  getByDate: (childId, date) =>
    api.get(`/api/summaries/${childId}/summaries/${date}`),
};

// Conversation Starters API
export const conversationStartersAPI = {
  get: (childId) =>
    api.get(`/api/children/${childId}/conversation-starters`),
  getCategorized: (childId) =>
    api.get(`/api/children/${childId}/conversation-starters`),
  getContextual: (childId, messageCount, recentMessages = null) =>
    api.post(`/api/children/${childId}/contextual-starters`, {
      message_count: messageCount,
      recent_messages: recentMessages
    })
};

// Child Settings API (Parent Controls)
export const childSettingsAPI = {
  get: (childId) =>
    api.get(`/api/children/${childId}/settings`),

  update: (childId, settings) =>
    api.patch(`/api/children/${childId}/settings`, settings),

  toggleAI: (childId, enabled) =>
    api.post(`/api/children/${childId}/settings/toggle-ai`, { enabled }),

  setSessionLimits: (childId, limits) =>
    api.post(`/api/children/${childId}/settings/session-limits`, limits),

  manageTopics: (childId, topics) =>
    api.post(`/api/children/${childId}/settings/manage-topics`, topics),

  getSessionUsage: (childId) =>
    api.get(`/api/children/${childId}/settings/session-usage`),
};

// Activities API
export const activitiesAPI = {
  get: (childId, limit = 20) =>
    api.get(`/api/children/${childId}/activities`, { params: { limit } }),

  updateStatus: (childId, activityId, data) =>
    api.patch(`/api/children/${childId}/activities/${activityId}`, data)
};

// Metrics API
export const metricsAPI = {
  getDaily: (childId, days = 7) =>
    api.get(`/api/metrics/daily/${childId}`, { params: { days } }),

  getSessions: (childId, days = 7) =>
    api.get(`/api/metrics/sessions/${childId}`, { params: { days } }),

  getWeekly: (childId, weeks = 4) =>
    api.get(`/api/metrics/weekly/${childId}`, { params: { weeks } }),

  trackHelpClick: (childId, sessionId = null, sessionDate = null) =>
    api.post('/api/metrics/track-help-click', {
      child_id: childId,
      session_id: sessionId,
      session_date: sessionDate
    }),

  updateSession: (childId, sessionId = null, sessionDate = null) =>
    api.post('/api/metrics/update-session', {
      child_id: childId,
      session_id: sessionId,
      session_date: sessionDate
    })
};

// Email Preferences API (Parent Account Settings)
export const emailPreferencesAPI = {
  get: () =>
    api.get('/api/email/preferences'),

  update: (preferences) =>
    api.put('/api/email/preferences', preferences),

  resubscribe: (emailType) =>
    api.post('/api/email/resubscribe', null, { params: { email_type: emailType } })
};

export default api;
